/* global Swiper */

/**
 * Initializes a double slider block with two Swiper instances and shared navigation.
 * Top slider moves forward, bottom slider moves backward.
 * Navigation buttons are disabled at the start/end for accessibility.
 * Navigation position updates responsively for each slider instance.
 *
 * @param {HTMLElement} block - The main .b-double-slider block element
 * @return {Object|null} References for global RWD update or null if not initialized
 */
const BREAKPOINT = 1320;

/**
 * Initialize a single double slider block.
 *
 * @param {HTMLElement} block - The double slider container element
 * @return {Object|null} References for global RWD update or null if not initialized
 */
const initializeDoubleSlider = ( block ) => {

	const sliders = block.querySelectorAll( ".b-quotes-slider .swiper" );
	if ( sliders.length < 2 ) {

		return null;

	}

	const nextBtn = block.querySelector( ".swiper-button-next" );
	const prevBtn = block.querySelector( ".swiper-button-prev" );
	const navigation = block.querySelector( ".b-double-slider__navigation" );
	const bottomSlider = block.querySelector( ".b-quotes-slider:last-child" );

	// Make navigation buttons keyboard accessible
	[nextBtn, prevBtn].forEach( ( btn ) => {

		btn.setAttribute( "tabindex", "0" );
		btn.setAttribute( "role", "button" );

	} );

	// Top slider: wider, slidesPerView: "auto"
	const topSwiper = new Swiper( sliders[0], {
		loop: false,
		speed: 800,
		grabCursor: true,
		keyboard: {
			enabled: true,
			onlyInViewport: true,
		},
		preloadImages: true,
		lazy: true,
		allowTouchMove: true,
		slidesPerView: "auto",
		spaceBetween: 32,
		initialSlide: 0,
		breakpoints: {
			0: {
				spaceBetween: 32,
				slidesPerView: 1,
			},
			768: {
				spaceBetween: 32,
				slidesPerView: 1,
			},
			1024: {
				spaceBetween: 32,
				slidesPerView: 1,
			},
			1300: {
				spaceBetween: 32,
				slidesPerView: "auto",
			},
		},
	} );

	// Bottom slider: fixed width, always 2 slides visible on desktop
	const bottomSwiper = new Swiper( sliders[1], {
		loop: false,
		speed: 800,
		grabCursor: true,
		keyboard: {
			enabled: true,
			onlyInViewport: true,
		},
		preloadImages: true,
		lazy: true,
		allowTouchMove: true,
		breakpoints: {
			0: {
				spaceBetween: 32,
				slidesPerView: 1,
			},
			768: {
				spaceBetween: 32,
				slidesPerView: 1,
			},
			1024: {
				spaceBetween: 32,
				slidesPerView: 1,
			},
			1300: {
				spaceBetween: 32,
				slidesPerView: 2,
			},
		},
	} );

	// Start bottom slider at the last slide for reverse navigation effect
	bottomSwiper.slideTo( bottomSwiper.slides.length - 1, 0, false );

	/**
	 * Updates navigation button states (disabled/enabled) for accessibility.
	 * @return {void}
	 */
	const updateNavigation = () => {

		const isTopAtStart = topSwiper.isBeginning;
		const isTopAtEnd = topSwiper.isEnd;
		const isBottomAtStart = bottomSwiper.isBeginning;
		const isBottomAtEnd = bottomSwiper.isEnd;

		// Disable "prev" if both sliders are at their respective starts
		if ( isTopAtStart && isBottomAtEnd ) {

			prevBtn.classList.add( "swiper-button-disabled" );
			prevBtn.setAttribute( "aria-disabled", "true" );

		} else {

			prevBtn.classList.remove( "swiper-button-disabled" );
			prevBtn.setAttribute( "aria-disabled", "false" );

		}

		// Disable "next" if both sliders are at their respective ends
		if ( isTopAtEnd && isBottomAtStart ) {

			nextBtn.classList.add( "swiper-button-disabled" );
			nextBtn.setAttribute( "aria-disabled", "true" );

		} else {

			nextBtn.classList.remove( "swiper-button-disabled" );
			nextBtn.setAttribute( "aria-disabled", "false" );

		}

	};

	/**
	 * Updates navigation position depending on breakpoint.
	 * On desktop: navigation is positioned near the bottom slider (right).
	 * On mobile/tablet: navigation is positioned at the right, always the same offset from the bottom of the first slider.
	 * @return {void}
	 */
	const updateNavigationPosition = () => {

		if ( navigation && bottomSlider ) {

			window.requestAnimationFrame( () => {

				if ( window.innerWidth >= BREAKPOINT ) {

					// Desktop: position navigation near bottom slider, right side
					const rect = bottomSlider.getBoundingClientRect();
					const parentRect = block.getBoundingClientRect();
					navigation.style.position = "absolute";
					navigation.style.top = `${rect.top - parentRect.top}px`;
					navigation.style.right = "69px";
					navigation.style.transform = "none";
					navigation.style.zIndex = "2";
					navigation.style.left = "auto";

				} else {

					// Mobile/tablet: position navigation at right, always same offset from first slider
					const firstSlider = block.querySelector(
						".b-quotes-slider:first-child",
					);
					if ( firstSlider ) {

						const firstRect = firstSlider.getBoundingClientRect();
						const parentRect = block.getBoundingClientRect();
						const offset = 20; // Offset in px from the bottom of the first slider
						navigation.style.position = "absolute";
						navigation.style.left = "auto";
						navigation.style.right = "0";
						navigation.style.top = `${firstRect.bottom - parentRect.top + offset}px`;
						navigation.style.transform = "none";
						navigation.style.zIndex = "2";

					}

				}

			} );

		}

	};

	// Update navigation on slide changes
	topSwiper.on( "slideChange", updateNavigation );
	bottomSwiper.on( "slideChange", updateNavigation );

	// Initial state
	updateNavigation();
	updateNavigationPosition();

	// Navigation click handlers
	nextBtn.addEventListener( "click", () => {

		if ( !nextBtn.classList.contains( "swiper-button-disabled" ) ) {

			topSwiper.slideNext();
			bottomSwiper.slidePrev();

		}

	} );
	prevBtn.addEventListener( "click", () => {

		if ( !prevBtn.classList.contains( "swiper-button-disabled" ) ) {

			topSwiper.slidePrev();
			bottomSwiper.slideNext();

		}

	} );

	// Keyboard accessibility: Enter/Space triggers navigation
	[nextBtn, prevBtn].forEach( ( btn ) => {

		btn.addEventListener( "keydown", ( e ) => {

			if ( e.key === "Enter" || e.key === " " ) {

				e.preventDefault();
				if (
					btn === nextBtn &&
					!nextBtn.classList.contains( "swiper-button-disabled" )
				) {

					topSwiper.slideNext();
					bottomSwiper.slidePrev();

				}
				if (
					btn === prevBtn &&
					!prevBtn.classList.contains( "swiper-button-disabled" )
				) {

					topSwiper.slidePrev();
					bottomSwiper.slideNext();

				}

			}

		} );

	} );

	// Return references for global RWD update
	return { block, navigation, bottomSlider, updateNavigationPosition };

};

/**
 * Initializes all double slider blocks and collects references.
 * @return {Array<Object>} Array of references for global RWD update
 */
const allSliders = Array.from( document.querySelectorAll( ".b-double-slider" ) )
	.map( initializeDoubleSlider )
	.filter( Boolean );

/**
 * Updates navigation position for all double slider instances on RWD events.
 * @return {void}
 */
const updateAllNavigationPositions = () => {

	allSliders.forEach( ( { updateNavigationPosition } ) => {

		if ( typeof updateNavigationPosition === "function" ) {

			updateNavigationPosition();

		}

	} );

};

// Global RWD listeners for all sliders
window.addEventListener( "resize", updateAllNavigationPositions );
const mediaQuery = window.matchMedia( `(min-width:${BREAKPOINT}px)` );
mediaQuery.addEventListener( "change", updateAllNavigationPositions );
